import React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { useGenerateRefs } from "Catalog/Grid/shared/components/GenerateRefs"
import GridLayout from "Catalog/Grid/shared/components/GridLayout"
import GridTable from "Catalog/Grid/shared/components/GridTable"
import ManageVariants from "Catalog/Grid/shared/components/ManageVariants"
import CatalogToolbar from "Catalog/shared/components/CatalogToolbar"
import { useStatefulFieldRemount } from "Catalog/shared/components/StatefulFieldRemountContext"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { DealId } from "shared/hubrise/ids"
import { IDeal } from "shared/hubrise/models"
import * as Deal from "shared/hubrise/utils/Deal"
import * as Restrictions from "shared/hubrise/utils/Restrictions"

const Deals = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ dealId: string }>()

  const { editor, catalog } = useEditor()
  const { remount } = useStatefulFieldRemount()

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.gridPath("deals")

  const toolbar = <CatalogToolbar commandActions={<ManageVariants />} />

  const items = catalog.data.deals

  const selectedItem = useFindItemOrRedirect<IDeal>(params.dealId, (id) => Deal.byId(catalog, DealId(id)), indexPath)

  React.useEffect(() => {
    visit("deal", selectedItem)
  }, [selectedItem, visit])

  useKeyboardNavigation<IDeal>({
    item: selectedItem,
    collection: items,
    indexPath: () => indexPath,
    showPath: (deal) => catalogNavigation.gridPath("deal", deal.id),
  })

  const removeAllRefs = () => {
    editor.deal.removeAllRefs()
    remount()
  }

  const { generateRefsModal, openGenerateRefs } = useGenerateRefs("deal", (options) => {
    editor.deal.generateRefs(options)
    remount()
  })

  const enableAll = (enabled: boolean, variantRef?: string) => {
    editor.deal.enableAll(enabled, variantRef)
    remount()
  }

  const main =
    items.length > 0 ? (
      <>
        {generateRefsModal}

        <GridTable
          items={items}
          selectedItem={selectedItem}
          designatorsHeaders={[t("grid.deal")]}
          removeAllRefs={removeAllRefs}
          openGenerateRefs={openGenerateRefs}
          variants={catalog.data.variants}
          enableAll={enableAll}
          selectItemPath={(deal) => catalogNavigation.gridPath("deal", deal.id)}
          editItemPath={(deal) => catalogNavigation.editPath("deal", deal.id)}
          previewItemPath={(deal) => catalogNavigation.previewPath("deal", deal.id)}
          designators={(deal) => [deal.name]}
          itemRef={(deal) => deal.ref}
          setRef={(deal, ref) => editor.deal.update(deal.id, "ref", ref)}
          catalogPrice={undefined}
          setCatalogPrice={undefined}
          variantPrice={undefined}
          setVariantPrice={undefined}
          isEnabled={(deal, variantRef) => Restrictions.isEnabled(deal.restrictions, variantRef)}
          enable={(deal, enabled, variantRef) => editor.deal.enable(deal.id, enabled, variantRef)}
        />
      </>
    ) : null

  return <GridLayout toolbar={toolbar} main={main} none={t("deal.none")} testId="grid-deals" />
}

export default Deals
